import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  DollarSign,
  Target,
  Zap,
  ArrowRight,
  Play,
  BarChart3,
  MessageSquare,
  Calendar,
  Star,
  Eye,
  Clock,
  Mail,
  Phone,
  Building,
  Activity,
  Sparkles,
  Rocket,
  Award,
  ChevronUp,
  ExternalLink,
  Plus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button/Button';
import { cn } from '../utils/cn';

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = "" 
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  onClick?: () => void;
}> = ({ title, value, change, icon: Icon, gradient, description, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.02 }}
      onClick={onClick}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <ChevronUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Quick Action Card Component
const QuickActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  gradient: string;
  onClick: () => void;
}> = ({ title, description, icon: Icon, gradient, onClick }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 cursor-pointer"
    >
      <div className="flex items-center space-x-4">
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold mb-1">{title}</h3>
          <p className="text-neutral-400 text-sm">{description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-neutral-500 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
      </div>
    </motion.div>
  );
};

// Activity Item Component
const ActivityItem: React.FC<{
  title: string;
  description: string;
  time: string;
  type: 'response' | 'campaign' | 'contact' | 'success';
}> = ({ title, description, time, type }) => {
  const typeConfig = {
    response: { icon: MessageSquare, color: 'text-blue-400' },
    campaign: { icon: Rocket, color: 'text-purple-400' },
    contact: { icon: Users, color: 'text-green-400' },
    success: { icon: Award, color: 'text-orange-400' },
  };

  const { icon: Icon, color } = typeConfig[type];

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-start space-x-4 p-4 hover:bg-neutral-800/30 rounded-xl transition-colors"
    >
      <div className={cn("w-2 h-2 rounded-full mt-3", color.replace('text-', 'bg-'))} />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">{title}</p>
        <p className="text-neutral-400 text-sm">{description}</p>
        <p className="text-neutral-500 text-xs mt-1">{time}</p>
      </div>
      <Icon className={cn("w-4 h-4 flex-shrink-0", color)} />
    </motion.div>
  );
};

const DashboardPageRevamped: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [timeOfDay, setTimeOfDay] = useState('');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setTimeOfDay('morning');
    else if (hour < 17) setTimeOfDay('afternoon');
    else setTimeOfDay('evening');
  }, []);

  const metrics = [
    {
      title: 'Total Campaigns',
      value: 12,
      change: '+23% this month',
      icon: Rocket,
      gradient: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      description: '8 active, 3 completed, 1 draft'
    },
    {
      title: 'Response Rate',
      value: '18.4%',
      change: '+5.2% vs last month',
      icon: TrendingUp,
      gradient: 'bg-gradient-to-r from-green-500 to-emerald-500',
      description: 'Above industry average'
    },
    {
      title: 'Total Contacts',
      value: 2847,
      change: '+340 this week',
      icon: Users,
      gradient: 'bg-gradient-to-r from-purple-500 to-pink-500',
      description: 'Across all campaigns'
    },
    {
      title: 'Revenue Generated',
      value: '$47,200',
      change: '+28% this quarter',
      icon: DollarSign,
      gradient: 'bg-gradient-to-r from-orange-500 to-red-500',
      description: 'From closed deals'
    }
  ];

  const quickActions = [
    {
      title: 'Smart Setup',
      description: 'AI-guided campaign setup in 10 minutes',
      icon: Sparkles,
      gradient: 'bg-gradient-to-r from-yellow-500 to-orange-600',
      action: () => navigate('/onboarding')
    },
    {
      title: 'Create New Campaign',
      description: 'Launch a targeted outreach campaign',
      icon: Rocket,
      gradient: 'bg-gradient-to-r from-blue-500 to-purple-600',
      action: () => navigate('/campaigns/new')
    },
    {
      title: 'Find Companies',
      description: 'Search for target organizations',
      icon: Building,
      gradient: 'bg-gradient-to-r from-green-500 to-emerald-600',
      action: () => navigate('/search/companies')
    },
    {
      title: 'Find People',
      description: 'Discover new prospects and contacts',
      icon: Users,
      gradient: 'bg-gradient-to-r from-purple-500 to-pink-600',
      action: () => navigate('/search/people')
    },
    {
      title: 'View Analytics',
      description: 'Deep dive into performance metrics',
      icon: BarChart3,
      gradient: 'bg-gradient-to-r from-orange-500 to-red-600',
      action: () => navigate('/analytics')
    }
  ];

  const recentActivity = [
    {
      title: 'New response from Sarah Chen',
      description: 'Q1 Enterprise Outreach campaign',
      time: '2 hours ago',
      type: 'response' as const
    },
    {
      title: 'Campaign "Data Platform" launched',
      description: 'Targeting 240 prospects',
      time: '4 hours ago',
      type: 'campaign' as const
    },
    {
      title: '25 new contacts imported',
      description: 'From TechFlow Solutions list',
      time: '6 hours ago',
      type: 'contact' as const
    },
    {
      title: 'Holiday campaign completed',
      description: '78 responses, 12 meetings booked',
      time: '1 day ago',
      type: 'success' as const
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Main Content - Account for left sidebar */}
      <div className="transition-all duration-300">
        <div className="relative">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 py-8">
            {/* Welcome Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    Good {timeOfDay}, {user?.firstName}! 👋
                  </h1>
                  <p className="text-xl text-neutral-400 mb-4">
                    Here's what's happening with your outreach campaigns
                  </p>
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-neutral-300">All systems operational</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-neutral-400" />
                      <span className="text-neutral-400">Last updated: {new Date().toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="hidden md:flex items-center space-x-3">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
                    onClick={() => navigate('/onboarding')}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Smart Setup
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
                  >
                    <BarChart3 className="w-5 h-5 mr-2" />
                    View Reports
                  </Button>
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    New Campaign
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Key Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {metrics.map((metric, index) => (
                  <motion.div
                    key={metric.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * (index + 1) }}
                  >
                    <MetricCard {...metric} />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-2"
              >
                <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">Quick Actions</h2>
                        <p className="text-neutral-400 text-sm">Jump into your most common tasks</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full px-3 py-1">
                      <Sparkles className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-300 text-sm">AI-Powered</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {quickActions.map((action, index) => (
                      <motion.div
                        key={action.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                      >
                        <QuickActionCard {...action} onClick={action.action} />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Recent Activity */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                        <Activity className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">Recent Activity</h2>
                        <p className="text-neutral-400 text-sm">Latest updates</p>
                      </div>
                    </div>
                    <button className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
                      View All
                    </button>
                  </div>
                  
                  <div className="space-y-1">
                    {recentActivity.map((activity, index) => (
                      <motion.div
                        key={activity.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                      >
                        <ActivityItem {...activity} />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Performance Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Today's Performance</h2>
                      <p className="text-neutral-400 text-sm">Real-time campaign metrics</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
                    onClick={() => navigate('/analytics')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400 mb-1">
                      <AnimatedCounter end={234} />
                    </div>
                    <div className="text-neutral-400 text-sm">Emails Sent</div>
                    <div className="text-green-400 text-xs mt-1">+12% vs yesterday</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-400 mb-1">
                      <AnimatedCounter end={18} suffix="%" />
                    </div>
                    <div className="text-neutral-400 text-sm">Open Rate</div>
                    <div className="text-green-400 text-xs mt-1">+3.2% improvement</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-400 mb-1">
                      <AnimatedCounter end={47} />
                    </div>
                    <div className="text-neutral-400 text-sm">Responses</div>
                    <div className="text-green-400 text-xs mt-1">+8 new today</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-400 mb-1">
                      <AnimatedCounter end={12} />
                    </div>
                    <div className="text-neutral-400 text-sm">Meetings Booked</div>
                    <div className="text-green-400 text-xs mt-1">+4 this week</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPageRevamped; 